import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Dropdown, Form, Row, Col, InputGroup } from 'react-bootstrap';
import _ from 'lodash';
import cx from 'classnames';
import Select from 'react-select';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';

import { segmentConditions } from 'constants/filters';

import styles from './segmentFilter.module.scss';
import { STORYBOARD_TYPES } from 'constants/storyboardType';

export const SegmentContext = React.createContext([]);

export const SegmentFilter = ({ index, value, onChange, onInsert, onDelete, canDelete, showAddFilter, inline, filters, filterFor }) => {
  const properties = React.useContext(SegmentContext);
  const isJourneyFilter = filterFor == "journey" ;
  const [filterAll, setFilterAll] = useState(_.get(value, 'operator') === "IS_ALL");
  const values = _.get(value, 'value', []) || [];
  const colStylesCondition = values.length > 0;
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));

  
  const handlePropertyChange = (item) => {
    var filter = {
      ...value,
      property: item,
      operator: 'IS',
      value: null
    };
    onChange(filter);
    setFilterAll(false);
  }

  const handleConditionChange = (operator) => {
    var filter = {
      ...value,
      operator: operator
    };
    
    if (operator === 'IS_EMPTY' || operator === 'NOT_EMPTY') {
      filter.value = [];
    }
    
    onChange(filter);

    if(operator === "IS_ALL") {
      setFilterAll(true);
    } else {
      setFilterAll(false);
    }
  }

  const handleValueChange = (changedValue) => {
    var filter = {
      ...value,
      value: changedValue,
    };
    onChange(filter);
    setFilterAll(false);
  }

  const handleDelete = () => {
    var filter = {
      ...value,
      operator: 'IS'
    };
    onChange(filter);
    setFilterAll(!filterAll);
  }
  
  return (
    <Row className={cx(styles.assetFilterRow, 'mx-0', {[styles.journeyAssetFilterRow]: isJourneyFilter })}>
      <Col className={styles.conditional}>
        <PropertyTypeFilter options={properties} value={value.property} onChange={handlePropertyChange} />
        {!filterAll &&
          <ConditionFilter value={value} onChange={handleConditionChange} filterIndex={index} filters={filters}/>
        }
        <div className={cx(styles.filterValues, { [styles.journeyFilterValues]: colStylesCondition })}>
          {!filterAll && value.operator !== 'IS_EMPTY' && value.operator !== 'NOT_EMPTY' &&
            <ValueComponent value={value} onChange={handleValueChange} filters={filters} inline={inline} filterFor={filterFor} />
          }
          {filterAll &&
            <FilterAllValueComponent onDelete={handleDelete} />
          }
        </div>
      </Col>
      
      <Col className={cx(styles.actions, {[styles.journeyActions]: (isJourneyFilter && showAddFilter) })}>
        {showAddFilter &&
          <Button className={styles.addButton} onClick={onInsert} variant="primary" size='sm' disabled={filterAll}>
            <i className={cx(styles.icon, 'cleverstory-plus')} />
            Add Filter
          </Button>
        }
        {canDelete &&
          <Button variant="info" onClick={() => { onDelete(index) }} className={styles.closeButton}>
            <i className={cx(styles.icon, 'cleverstory-close')} />
          </Button>
        }
      </Col>
    </Row>
  )
}

const PropertyTypeFilter = ({ value, disabled, onChange }) => {
  const properties = React.useContext(SegmentContext);

  const property = properties.find(item => {
    if (value.type === 'CUSTOM_FIELDS' || value.type === 'LEAD') {
      return value.id === item.value.id;
    }
    return item.value.type === value.type
  });

  const [searchTerm, setSearchTerm] = useState('');
  const filteredItems = useMemo(() => {
    const filtered = properties.filter(item => {
      if (!searchTerm) return true;
      return item.label?.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    return splitFilterOptions(filtered);
  }, [properties, searchTerm]);
      
  const handleChange = (data) => {
    const selectedOption = properties.find(p => {
      return p.value === data.value;
    })
    onChange(selectedOption.value);
  }

  function splitFilterOptions(flattenedArray) {
    const result = {
      data: [],
      leadData: [],
      custom: []
    };

    flattenedArray.forEach(item => {
      const type = item?.value?.type;
  
      if (!type) return;
  
      switch (type) {
        case 'CUSTOM_FIELDS':
          // result.custom.push(item);
          break;
        case 'LEAD':
          // result.leadData.push(item);
          break;
        default:
          result.data.push(item); // default = static segmentFilterOptions
          break;
      }
    });
  
    return result;
  }

  return (
    <Dropdown disabled={disabled} className={styles.filterDropdown}>
      <Dropdown.Toggle className={styles.selected}>
        <Button size='sm' variant='primary' className={styles.button}>
          <span className={styles.filterType}>
            <TruncatedTextWithTooltip content={_.get(property, 'label')} maxWidth={230} placement='top'/>
          </span>
          <i className={cx(styles.icon, 'cleverstory-drop-down')} />
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Menu
          className={styles.filterMenu}
        >
            <InputGroup className={styles.searchBox}>
              <InputGroup.Text className={styles.icon}>
                <i className={cx("cleverstory-search")} />
              </InputGroup.Text>
              <Form.Control
                className={styles.inputField}
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </InputGroup>

          {filteredItems.leadData.length > 0 || filteredItems.custom.length > 0 || filteredItems.data.length > 0 ? (
            <>
              {filteredItems.data.length > 0 && (
                <>
                  <span className={styles.dropdownSectionLabel}>DATA</span>
                  {filteredItems.data.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => handleChange(item)}
                      // active={selectedItem?.id === item.id}
                    >
                      <TruncatedTextWithTooltip content={item.label} maxWidth={200} placement='top'/>
                    </Dropdown.Item>
                  ))}
                </>
              )}
              {filteredItems.leadData.length > 0 && (
                <>
                  <span className={styles.dropdownSectionLabel}>LEAD DATA</span>
                  {filteredItems.leadData.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => handleChange(item)}
                      // active={selectedItem?.id === item.id}
                    >
                      <TruncatedTextWithTooltip content={item.label} maxWidth={200} placement='top'/>
                    </Dropdown.Item>
                  ))}
                </>
              )}
              {filteredItems.custom.length > 0 && (
                <>
                  <span className={styles.dropdownSectionLabel}>CUSTOM FIELD</span>
                  {filteredItems.custom.map((item) => (
                    <Dropdown.Item
                      key={item.id}
                      onClick={() => handleChange(item)}
                      // active={selectedItem?.id === item.id}
                    >
                      <TruncatedTextWithTooltip content={item.label} maxWidth={200} placement='top'/>
                    </Dropdown.Item>
                  ))}
                </>
              )}
            </>
          ) : (
            <span className={styles.noResults}>
              {_.isEmpty(searchTerm) ? `No Options` : `No matching options found`}
            </span>
          )}
        </Dropdown.Menu>
    </Dropdown>
  )
}

const ConditionFilter = ({ value, disabled, onChange, filterIndex, filters }) => {
  const properties = React.useContext(SegmentContext);
  
  const currentProperty = properties.find(item => {
    if (value.property.type === 'CUSTOM_FIELDS' || value.property.type === 'LEAD') {
      return value.property.id === item.value.id;
    }
    return item.value.type === value.property.type;
  });
  
  const dataType = _.get(currentProperty, 'data_type', null);
  
  const condition = segmentConditions.find(item => item.type === 'tags') || {};
  
  const filteredConditions = dataType === 'string' 
    ? condition.options || []
    : (condition.options || []).filter(opt => opt.value === 'IS' || opt.value === 'IS_NOT');
  
  const operator = filteredConditions.find(item => item.value === value.operator);
  const canFilterAll = filters.length <= 1 && filterIndex == 0; 

  const handleChange = (data) => {
    const value = data.value;
    onChange(value);
  }

  return (
    <Dropdown disabled={disabled} className={styles.filterDropdown}>
      <Dropdown.Toggle className={styles.selected}>
        <Button size='sm' variant='primary' className={styles.button}>
          <span className={styles.filterType}>
            {operator?.label || 'Select'}
          </span>
          <i className={cx(styles.icon, 'cleverstory-drop-down')} />
        </Button>
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.typesMenu}>
        {
          filteredConditions.map((data, index) => {
            if(!canFilterAll && data.value == "IS_ALL") {
                return null;
            } 
            return (
              <Dropdown.Item onClick={() => { handleChange(data) }} key={index}>
                {data.label}
              </Dropdown.Item>
            )
          })
        }
      </Dropdown.Menu>
    </Dropdown>
  )
}

/**
 * Value Input
*/
function ValueComponent({ value, disabled, onChange, inline, filterFor, filters }) {
  const properties = React.useContext(SegmentContext);

  const property = properties.find(item => {
    if (value.property.type === 'CUSTOM_FIELD') {
      return value.property.fieldId === item.value.fieldId;
    }
    return item.value.type === value.property.type;
  });

  var type = _.get(property, 'data_type', null);
  if (type === 'list' || type === 'string') {
    return (
      <ListValue property={property}
        value={value}
        onChange={onChange}
        disabled={disabled}
        filterFor={filterFor}
      />
    );
  }
  return (
    <InputValue property={property}
      value={value}
      filters={filters}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

function ListValue({ property, value, disabled, onChange, filterFor }) {
  const journeyAssetTypes = ["INTERACTIVE_CONTENT" , "LANDING_PAGE", "RESOURCE_CENTER", STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2];
  const isJourneyFilter = filterFor == "journey" ;
  var options = _.get(property, 'config.options', []);
  var type = _.get(property, 'data_type', null);
  const values = _.get(value, 'value', []) || [];
  const [inputValue, setInputValue] = React.useState('');

  const filteredOptions = isJourneyFilter 
    ? options.filter(option => !journeyAssetTypes.includes(option.value))
    : options;
  
  const handleSelect = (option) => {
    var request = [...values];
    request = [...request, option];
    request = _.uniq(request);
    onChange(request);
  }

  const handleKeyDown = (event, data) => {
    if (event.key === 'Enter') {
      handleSelect(data);
    }
  };

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' && inputValue.trim()) {
      event.preventDefault();
      handleSelect(inputValue.trim());
      setInputValue('');
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      handleSelect(inputValue.trim());
      setInputValue('');
    }
  };

  const handleDelete = (option) => {
    var request = [...values].filter(item => item !== option);
    onChange(request);
  }

  return (
    <>
      {values.map((item, index) => {
        var option = options.find(o => {
          return o.value === item;
        })
        
        if (type === 'string') {
          const displayLabel = option ? option.label : item;
          const displayIcon = option ? option.icon : null;
          
          return (
            <div key={`${item}-${index}`} className={cx(styles.filterTags)}>
              {displayIcon &&
                  <i className={cx(styles.filterTypeIcon, `cleverstory-${displayIcon}`)} />
              }
              <TruncatedTextWithTooltip content={displayLabel} className={styles.name} maxWidth={230} placement='top' />
              <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={() => handleDelete(item)} />
            </div>
          );
        } else {
          if (!option) {
            return null;
          }
          return (
            <div key={`${item}-${index}`} className={cx(styles.filterTags)}>
              {option.icon &&
                  <i className={cx(styles.filterTypeIcon, `cleverstory-${option.icon}`)} />
              }
              <TruncatedTextWithTooltip content={option.label} className={styles.name} maxWidth={230} placement='top' />
              <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={() => handleDelete(item)} />
            </div>
          );
        }
      })}

      {type == 'string' ? (
        <Form className={styles.form}>
          <Form.Group className={styles.field}>
            <Form.Control
              className={styles.control}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={handleInputBlur}
              placeholder="Type here"
              disabled={disabled}
            />
          </Form.Group>
        </Form>
      ) : (
        <Dropdown className={styles.filesDropdown} onSelect={handleSelect} disabled={disabled}>
          <Dropdown.Toggle variant="success" className={styles.selected} id="dropdown-basic">
            <Button size='sm' variant='primary' className={styles.button}>
              Select
            </Button>
          </Dropdown.Toggle>
          <Dropdown.Menu className={styles.typesMenu}>
            {filteredOptions.map((data, index) => {
              return (
                <Dropdown.Item key={index} eventKey={data.value} onKeyDown={(e) => handleKeyDown(e, data.value)}>
                  <i className={cx(styles.filterTypeIcon, `cleverstory-${data.icon}`)} />
                  <TruncatedTextWithTooltip content={data.label} maxWidth={250} placement='top'/>
                </Dropdown.Item>
              )
            })}
          </Dropdown.Menu>
        </Dropdown>
      )}
    </>
  )
}

function InputValue({ value, onChange, filters }) {
  const values = _.get(value, 'value', []) || [];
  const [query, setQuery] = useState('');
  const [countries, setCountries] = useState([]);
    // Get selected continents from existing filters
    const selectedContinents = useMemo(() => {
      const continentFilters = filters.filter(
        (filter) => filter.property.type === 'CONTINENT' && Array.isArray(filter.value)
      );
      return _.uniq(continentFilters.flatMap((f) => f.value.map((v) => v.toLowerCase())));
    }, [filters]);

    useEffect(() => {
      fetch('/countries.json')
        .then((res) => res.json())
        .then((data) => {
          setCountries(data);
        })
        .catch((err) => {
        });
    }, []);
  
    // Filter countries by selected continents
    const options = useMemo(() => {
      let countryOptions = countries;
      if (selectedContinents.length > 0) {
        countryOptions = countryOptions.filter((c) =>
          selectedContinents.includes(c.continent.toLowerCase())
        );
      }
  
      return countryOptions.map((c) => ({ label: c.label, value: c.value }));
    }, [selectedContinents, countries]);
  
    const handleChange = (selected) => {
      const request = _.uniq([...values, selected.value]);
      onChange(request);
      setQuery('');
    };

  const handleKeyPressed = (e) => {
    if(e.key === "Enter") {
      handleChange();
    }
  }

  const handleDelete = (option) => {
    var request = [...values].filter(item => item !== option);
    onChange(request);
  }
    
  return (
    <Fragment>
      {values.map((item, index) => {
        var option = options.find(o => {
          return o.value === item;
        })
        if(!option) {
          return false;
        }
        return (
          <div key={`${item}-${index}`} className={cx(styles.filterTags)}>
            <TruncatedTextWithTooltip content={option.label} className={styles.name} maxWidth={200} placement='top'/>
            <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={() => handleDelete(item)} />
          </div>
        );
      })}
      <Select
        className={styles.tagsDropdown}
        classNamePrefix={"tagsDropdown"}
        options={options}
        onChange={handleChange}
        value={null}
        isSearchable={true}
        menuPosition="absolute"
        menuShouldScrollIntoView={false}
        placeholder="Search"
        menuPlacement="auto"
        styles={{
          control: (base) => ({ ...base, minHeight: '36px' })
        }}
      />
    </Fragment>
  );
}

function FilterAllValueComponent({onDelete}) {
  return (
    <div className={cx(styles.filterTags)}>
      <TruncatedTextWithTooltip content='Is All' className={styles.name} maxWidth={230} placement='top' />
      <i className={cx(styles.closeIcon, `cleverstory-close`)} onClick={onDelete} />
    </div>
  );
}